import axios from 'axios'
import get from 'lodash/fp/get'

export default {
  state: {
    devices: [],
    livionDevices: [],
  },
  mutations: {
    updateIOTDevices(state, devices) {
      state.devices = devices
    },
    updateIOTEvents(state, events) {
      state.events = events
    },
    updateLivionDevices(state, devices) {
      state.livionDevices = devices
    },
    updateLivionDevice(state, updatedDevice) {
      const index = state.livionDevices.findIndex(
        d => d.id === updatedDevice.id
      )
      if (index !== -1) {
        state.livionDevices.splice(index, 1, updatedDevice)
      }
    },
  },
  actions: {
    async getIOTDevices({ commit }) {
      try {
        const { data } = await axios.get(`/api/iot-devices`)
        commit('updateIOTDevices', data)
      } catch (err) {
        console.error(err)
      }
    },
    async updateMinutHome({ dispatch }, { id, ...body }) {
      try {
        const res = await axios.put(`/api/iot-devices/minut/home/${id}`, body)
        dispatch('getIOTDevices')
        return res
      } catch (err) {
        return err
      }
    },
    async getIOTEvents({ commit }, { listingId }) {
      try {
        const { data } = await axios.get(
          `/api/listings/${listingId}/iot-events`
        )
        commit('updateIOTEvents', data)
      } catch (err) {
        console.error(err)
      }
    },
    async getLivionDevices({ commit }) {
      try {
        const { data } = await axios.get('/api/iot/livion-devices')
        if (data.success) {
          commit('updateLivionDevices', data.payload.data.automats || [])
          return data.payload.data
        } else {
          throw new Error(data.error || 'Failed to load automats')
        }
      } catch (err) {
        console.error('Failed to load LivionKey automats:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to load automats'
        )
      }
    },
    async syncLivionAutomat({ dispatch }, { automat_id, listing_id }) {
      try {
        const { data } = await axios.post('/api/iot/livion-devices/sync', {
          automat_id,
          listing_id,
        })
        if (data.success) {
          await dispatch('getLivionDevices')
          return data.payload.data
        } else {
          throw new Error(data.error || 'Failed to sync automat')
        }
      } catch (err) {
        console.error('Failed to sync LivionKey automat:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to sync automat'
        )
      }
    },
    async updateIotDevice({ commit }, payload) {
      try {
        const { data } = await axios.patch(
          `/api/iot/livion-devices/${payload.id}`,
          {
            iot_device: payload,
          }
        )
        if (data.success) {
          commit('updateLivionDevice', data.payload.data.device)
          return data.payload.data
        } else {
          throw new Error(data.error || 'Failed to update device')
        }
      } catch (err) {
        console.error('Failed to update IoT device:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to update device'
        )
      }
    },
    async toggleLivionDevice({ commit, state }, deviceId) {
      try {
        const { data } = await axios.post(
          `/api/iot/livion-devices/${deviceId}/toggle-active`
        )
        if (data.success) {
          const device = state.livionDevices.find(
            d => d.boom_device_id === deviceId
          )
          if (device) {
            device.active = data.payload.data.active
          }
          return data.payload.data
        } else {
          throw new Error(data.error || 'Failed to toggle device')
        }
      } catch (err) {
        console.error('Failed to toggle IoT device:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to toggle device'
        )
      }
    },
    async deleteIotDevice({ dispatch }, deviceId) {
      try {
        const { data } = await axios.delete(
          `/api/iot/livion-devices/${deviceId}`
        )
        if (data.success) {
          await dispatch('getLivionDevices')
        } else {
          throw new Error(data.error || 'Failed to delete device')
        }
      } catch (err) {
        console.error('Failed to delete IoT device:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to delete device'
        )
      }
    },
    async createLivionKey({ dispatch }, { deviceId, keyData }) {
      try {
        const response = await axios.post(
          `/api/iot/livion-devices/${deviceId}/keys`,
          keyData
        )
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        } else if (data) {
          throw new Error(data.error || 'Failed to create key')
        } else {
          throw new Error('No response data received')
        }
      } catch (err) {
        console.error('Failed to create LivionKey:', err)
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to create key'
        throw new Error(errorMessage)
      }
    },
    async updateLivionKey({ dispatch }, { deviceId, keyId, keyData }) {
      try {
        const response = await axios.patch(
          `/api/iot/livion-devices/${deviceId}/keys/${keyId}`,
          keyData
        )
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        } else if (data) {
          throw new Error(data.error || 'Failed to update key')
        } else {
          throw new Error('No response data received')
        }
      } catch (err) {
        console.error('Failed to update LivionKey:', err)
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to update key'
        throw new Error(errorMessage)
      }
    },
    async deleteLivionKey({ dispatch }, { deviceId, keyId }) {
      try {
        const response = await axios.delete(
          `/api/iot/livion-devices/${deviceId}/keys/${keyId}`
        )
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        } else if (data) {
          throw new Error(data.error || 'Failed to delete key')
        } else {
          throw new Error('No response data received')
        }
      } catch (err) {
        console.error('Failed to delete LivionKey:', err)
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to delete key'
        throw new Error(errorMessage)
      }
    },
    async getAllLivionKeys() {
      try {
        const response = await axios.get('/api/iot/livion-keys')
        const data = response.data
        if (data && data.success) {
          return {
            keys: data.payload?.data?.keys || [],
            mappedKeys: data.payload?.data?.mapped_keys || {},
          }
        }
        throw new Error(data?.error || 'Failed to load keys')
      } catch (err) {
        console.error('Failed to load all LivionKey keys:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to load keys'
        )
      }
    },
    async importLivionKey(_, { keyId }) {
      try {
        const response = await axios.post('/api/iot/livion-keys/import', {
          key_id: keyId,
        })
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        }
        throw new Error(data?.error || 'Failed to import key')
      } catch (err) {
        console.error('Failed to import LivionKey:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to import key'
        )
      }
    },
    async deleteLivionKeyDirect(_, { keyId }) {
      try {
        const response = await axios.delete(`/api/iot/livion-keys/${keyId}`)
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        }
        throw new Error(data?.error || 'Failed to delete key')
      } catch (err) {
        console.error('Failed to delete LivionKey:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to delete key'
        )
      }
    },
    async updateLivionKeyDirect(_, { keyId, keyData }) {
      try {
        const response = await axios.patch(
          `/api/iot/livion-keys/${keyId}`,
          keyData
        )
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        }
        throw new Error(data?.error || 'Failed to update key')
      } catch (err) {
        console.error('Failed to update LivionKey:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to update key'
        )
      }
    },
    async getLivionApiKeys(_, { deviceId }) {
      try {
        const response = await axios.get(
          `/api/iot/livion-devices/${deviceId}/keys/api`
        )
        const data = response.data
        if (data && data.success) {
          return {
            keys: data.payload?.data?.keys || [],
            mappedKeys: data.payload?.data?.mapped_keys || {},
          }
        }
        throw new Error(data?.error || 'Failed to load keys')
      } catch (err) {
        console.error('Failed to load LivionKey API keys:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to load keys'
        )
      }
    },
    async importLivionApiKey(_, { deviceId, keyId }) {
      try {
        const response = await axios.post(
          `/api/iot/livion-devices/${deviceId}/keys/import`,
          { key_id: keyId }
        )
        const data = response.data
        if (data && data.success) {
          return data.payload?.data || data
        }
        throw new Error(data?.error || 'Failed to import key')
      } catch (err) {
        console.error('Failed to import LivionKey API key:', err)
        throw new Error(
          err.response?.data?.error || err.message || 'Failed to import key'
        )
      }
    },
  },
  getters: {
    devices(state) {
      return state.devices
    },
    listingDevices(state, getters, rooteState, rootGetters) {
      return listingId => {
        const currentListingId = listingId || rootGetters.currentListingId
        return state.devices.filter(
          d => get('listing.id', d) === currentListingId
        )
      }
    },
    livionDevices(state) {
      return state.livionDevices
    },
  },
}
